import React, { useState, useEffect } from 'react';
import { db } from '../services/db';
import { TimeEntry } from '../models/types';
import { 
  formatTime, calculateDailyTotal, calculateWeeklyTotal, 
  formatDuration, getDayOfWeek, getPreviousDay, getNextDay, isToday,
  calculateNetWorkDuration // Changed from calculateDuration
} from '../utils/timeUtils';
import '../styles/TimeEntriesTable.css';
import EditEntryModal from './EditEntryModal';

interface TimeEntriesTableProps {
  refreshTrigger: number;
}

const TimeEntriesTable: React.FC<TimeEntriesTableProps> = ({ refreshTrigger }) => {
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [dayEntries, setDayEntries] = useState<TimeEntry[]>([]);
  const [weeklyTotal, setWeeklyTotal] = useState<number>(0);
  const [dailyTotal, setDailyTotal] = useState<number>(0);
  const [selectedEntry, setSelectedEntry] = useState<TimeEntry | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);

  useEffect(() => {
    loadEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate, refreshTrigger]);

  const loadEntries = async () => {
    try {
      // Get entries for the current day
      const entries = await db.getTimeEntriesForDay(currentDate);
      setDayEntries(entries);
      
      // Calculate daily total
      const dayTotal = calculateDailyTotal(entries);
      setDailyTotal(dayTotal);
      
      // Get entries for the current week and calculate weekly total
      const weekEntries = await db.getTimeEntriesForWeek(currentDate);
      const weekTotal = calculateWeeklyTotal(weekEntries);
      setWeeklyTotal(weekTotal);
    } catch (error) {
      console.error('Error loading time entries:', error);
    }
  };

  const handlePreviousDay = () => {
    setCurrentDate(getPreviousDay(currentDate));
  };

  const handleNextDay = () => {
    const tomorrow = getNextDay(currentDate);
    const today = new Date();
    
    // Don't allow navigating to future days
    if (tomorrow.getTime() <= today.getTime()) {
      setCurrentDate(tomorrow);
    }
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleRowClick = (entry: TimeEntry) => {
    setSelectedEntry(entry);
    setIsEditModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsEditModalOpen(false);
    setSelectedEntry(null);
    loadEntries();
  };

  return (
    <div className="time-entries-container">
      <div className="date-navigation">
        <button onClick={handlePreviousDay}>&lt;&lt;</button>
        <button onClick={handleToday} className={isToday(currentDate) ? 'today-active' : ''}>
          {isToday(currentDate) ? 'Today' : getDayOfWeek(currentDate)}
        </button>
        <button onClick={handleNextDay} disabled={isToday(currentDate)}>&gt;&gt;</button>
        <div className="day-total">
          <span>Day Tot:</span>
          <span>{formatDuration(dailyTotal)}</span>
        </div>
      </div>

      <div className="time-entries-table">
        <table>
          <thead>
            <tr>
              <th>Time In</th>
              <th>Time Out</th>
              <th>Project:Task</th>
              <th>Loc</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {dayEntries.length > 0 ? (
              dayEntries.map((entry) => (
                <tr key={entry.id} onClick={() => handleRowClick(entry)}>
                  <td>{formatTime(entry.timeIn)}</td>
                  <td>{entry.timeOut ? formatTime(entry.timeOut) : '--:--'}</td>
                  <td>
                    {entry.project}
                    {entry.task ? `:${entry.task}` : ''}
                  </td>
                  <td>{entry.location}</td>
                  <td>
                    {entry.timeOut
                      ? formatDuration(calculateNetWorkDuration(entry)) // Changed to use calculateNetWorkDuration
                      : '--:--'}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className="no-entries">
                  No time entries for this day
                </td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={3}>Week Total</td>
              <td colSpan={2}>{formatDuration(weeklyTotal)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      {isEditModalOpen && selectedEntry && (
        <EditEntryModal
          entry={selectedEntry}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default TimeEntriesTable;
